import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { getCustomerIdTitles } from "../../services/customerService";
import { getCostCenterItems } from "../../services/costCenterService";
import { handleApiError } from "../../models/store/storeEffects";

interface DropdownOption {
    id: string;
    label: string;
}

interface FilterCostCenterOption {
    id: string;
    label: string;
    customerId: string;
}

export const useGeofenceFilter = () => {
    const dispatch = useAppDispatch();
    const filterCustomerIds = useAppSelector(
        (state) => state.geofence.filterCustomerIds
    );
    const filterCostCenterIds = useAppSelector(
        (state) => state.geofence.filterCostCenterIds
    );

    const [filterAnchorEl, setFilterAnchorEl] =
        useState<HTMLElement | null>(null);
    const [filterCustomers, setFilterCustomers] = useState<DropdownOption[]>(
        []
    );
    const [filterCostCenters, setFilterCostCenters] = useState<
        FilterCostCenterOption[]
    >([]);
    const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);

    useEffect(() => {
        if (!filterAnchorEl || filterOptionsLoaded) return;
        void Promise.all([
            getCustomerIdTitles(),
            getCostCenterItems(9999, 1, "", "name", true, false),
        ])
            .then(([custRes, ccRes]) => {
                setFilterCustomers(
                    (custRes?.items ?? []).map((c) => ({
                        id: c.id,
                        label: c.title,
                    }))
                );
                setFilterCostCenters(
                    (ccRes?.items ?? []).map((c) => ({
                        id: c.id,
                        label: c.name,
                        customerId: c.customerId,
                    }))
                );
                setFilterOptionsLoaded(true);
            })
            .catch((e) => handleApiError(e, dispatch));
    }, [filterAnchorEl, filterOptionsLoaded, dispatch]);

    // Mutual filtering: cost centers filtered by selected customers
    const availableCostCenters = useMemo(() => {
        if (!filterCustomerIds.length) return filterCostCenters;
        const ids = filterCustomerIds.map((id) => id.toLowerCase());
        return filterCostCenters.filter((cc) =>
            ids.includes(cc.customerId.toLowerCase())
        );
    }, [filterCostCenters, filterCustomerIds]);

    // Mutual filtering: customers filtered by selected cost centers
    const availableCustomers = useMemo(() => {
        if (!filterCostCenterIds.length) return filterCustomers;
        const ownerIds = new Set(
            filterCostCenters
                .filter((cc) => filterCostCenterIds.includes(cc.id))
                .map((cc) => cc.customerId.toLowerCase())
        );
        return filterCustomers.filter((c) => ownerIds.has(c.id.toLowerCase()));
    }, [filterCustomers, filterCostCenters, filterCostCenterIds]);

    return {
        filterAnchorEl,
        setFilterAnchorEl,
        filterCustomers,
        filterCostCenters,
        filterCustomerIds,
        filterCostCenterIds,
        availableCustomers,
        availableCostCenters,
    };
};
